import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera,
  CheckCircle2,
  FileCheck2,
  FileSignature,
  KeyRound,
  RotateCcw,
} from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { Eyebrow } from "@/components/common/Section";
import { usePrefersReducedMotion } from "@/hooks/useReducedMotion";

/* ============================================================
 * THE ASCENT — an interactive expedition panel that sits in the
 * normal page flow (no sticky, no scroll-pinning). One rounded
 * container hosts the ridgeline trail, six camp nodes, and a
 * two-column stage that swaps content as the active camp changes.
 * ============================================================ */

const CAMPS = [
  {
    key: "basecamp",
    label: "Basecamp",
    time: "Day 0",
    dayBig: "Day 0",
    title: "You sign — we start.",
    lines: [
      "Agreement signed.",
      "Within one hour: photographer booked, intake sent, comps pulled.",
    ],
    x: 80,
    y: 180,
  },
  {
    key: "camp-1",
    label: "Camp 1",
    time: "48h",
    dayBig: "48h",
    title: "Photographer on site.",
    lines: [
      "Professional photos + video shot inside 48 hours.",
      "Floor plan drawn. Copy written by someone who has stood in the room.",
    ],
    x: 280,
    y: 150,
  },
  {
    key: "camp-2",
    label: "Camp 2",
    time: "Day 4",
    dayBig: "Day 4",
    title: "Live everywhere at once.",
    lines: [
      "Live on RentFaster, Rentals.ca, Zumper, Kijiji, Facebook & Instagram.",
      "Every platform, the same afternoon — human-verified, not bot-syndicated.",
    ],
    x: 490,
    y: 110,
  },
  {
    key: "camp-3",
    label: "Camp 3",
    time: "Showings",
    dayBig: "Week 1",
    title: "Showings + Monday reports.",
    lines: [
      "Showings 7 days a week. Every inquiry answered inside the hour.",
      "Your report every Monday, 10am. What viewers said — verbatim.",
    ],
    x: 700,
    y: 125,
  },
  {
    key: "camp-4",
    label: "Camp 4",
    time: "Signed",
    dayBig: "~Day 10",
    title: "Screened & signed.",
    lines: [
      "Screened in 48 hours — credit, income, references.",
      "One clear recommendation. You approve. The lease is signed.",
    ],
    x: 900,
    y: 80,
  },
  {
    key: "summit",
    label: "Summit",
    time: "Move-in",
    dayBig: "Move-in",
    title: "Home is running.",
    lines: [
      "Lease signed, deposit secured, move-in day.",
      "The climb becomes cash flow.",
    ],
    x: 1120,
    y: 30,
  },
];

/* An organic climbing ridgeline through all 6 camp x/y coordinates.
 * Extra midpoints give it a natural saddle-and-crest feel. */
const PATH_D =
  "M80,180 L180,168 L280,150 L385,138 L490,110 L595,120 L700,125 L800,105 L900,80 L1010,60 L1120,30";

const Intro = () => (
  <div className="mx-auto max-w-[1440px] px-6 pt-24 md:px-10 md:pt-36">
    <Reveal>
      <Eyebrow color="#AEC4C7">For owners · The Ascent&trade;</Eyebrow>
    </Reveal>
    <Reveal delay={0.05}>
      <h2
        className="font-display mt-7 max-w-[18ch]"
        style={{
          fontSize: "clamp(36px,5vw,72px)",
          color: "#F8F5EF",
          lineHeight: 1.02,
        }}
      >
        Signed to listed everywhere in{" "}
        <span className="italic" style={{ color: "#E3B268" }}>
          four days.
        </span>
      </h2>
    </Reveal>
    <Reveal delay={0.1}>
      <p
        className="body-measure mt-6 text-[17px]"
        style={{ color: "rgba(237,233,224,0.72)" }}
      >
        Our lease-up system runs on hard deadlines, not promises.
      </p>
    </Reveal>
  </div>
);

const Afterword = () => {
  const stats = [
    { big: "48h", label: "photographer on site", off: "" },
    { big: "4 days", label: "live on every platform", off: "md:ml-14 md:mt-8" },
    {
      big: "<1h",
      label: "inquiry response, 7 days a week",
      off: "md:ml-6 md:-mt-4",
    },
  ];
  return (
    <div className="mx-auto max-w-[1440px] px-6 pb-28 md:px-10 md:pb-40">
      <div className="flex flex-col flex-wrap gap-6 md:flex-row md:items-start">
        {stats.map((s) => (
          <Reveal key={s.label} className={s.off}>
            <div
              className="rounded-full px-7 py-4"
              style={{ border: "1px solid rgba(174,196,199,0.35)" }}
              data-testid={`ascent-stat-${s.big
                .replace(/[^a-z0-9]/gi, "")
                .toLowerCase()}`}
            >
              <span
                className="font-display"
                style={{
                  fontSize: "clamp(24px,2.4vw,34px)",
                  color: "#AEC4C7",
                }}
              >
                {s.big}
              </span>
              <span
                className="ml-3 text-[15px]"
                style={{ color: "rgba(237,233,224,0.78)" }}
              >
                — {s.label}
              </span>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <Link
          to="/owners#analysis"
          className="pill pill-gold mt-12 text-base"
          data-testid="ascent-cta"
        >
          Get a free rent analysis
        </Link>
      </Reveal>
    </div>
  );
};

/* ============================================================
 * ExpeditionPanel — the whole interactive stage.
 * ============================================================ */
const ExpeditionPanel = () => {
  const reduced = usePrefersReducedMotion();
  const [active, setActive] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const panelRef = useRef(null);

  // Track viewport bucket.
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mql.matches);
    on();
    mql.addEventListener("change", on);
    return () => mql.removeEventListener("change", on);
  }, []);

  // Auto-advance every 4s until first interaction (or reduced-motion).
  useEffect(() => {
    if (hasInteracted || reduced) return;
    const id = setInterval(() => {
      setActive((a) => (a + 1) % CAMPS.length);
    }, 4000);
    return () => clearInterval(id);
  }, [hasInteracted, reduced]);

  const commit = (i) => {
    setHasInteracted(true);
    setActive(Math.max(0, Math.min(CAMPS.length - 1, i)));
  };
  const next = () => commit((active + 1) % CAMPS.length);
  const prev = () => commit((active - 1 + CAMPS.length) % CAMPS.length);
  const replay = () => {
    setHasInteracted(false);
    setActive(0);
  };

  // Arrow-key navigation when the panel has focus.
  const onKeyDown = (e) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      next();
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      prev();
    }
  };

  // Mobile / touch: swipe to advance.
  const touchStartX = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) (dx < 0 ? next : prev)();
    touchStartX.current = null;
  };

  const camp = CAMPS[active];
  const progress = CAMPS.length > 1 ? active / (CAMPS.length - 1) : 0;

  return (
    <div className="mx-auto max-w-[1440px] px-6 md:px-10">
      <motion.div
        ref={panelRef}
        tabIndex={0}
        role="region"
        aria-label="The Ascent — lease-up journey"
        onKeyDown={onKeyDown}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onMouseEnter={() => setHasInteracted(true)}
        initial={reduced ? { opacity: 0 } : { opacity: 0, y: 60 }}
        whileInView={
          reduced
            ? { opacity: 1, transition: { duration: 0.3 } }
            : {
                opacity: 1,
                y: 0,
                transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
              }
        }
        viewport={{ once: true, margin: "-80px" }}
        className="grain relative mt-16 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E3B268]"
        style={{
          borderRadius: 32,
          backgroundColor: "#14231B",
          border: "1px solid rgba(237,233,224,0.08)",
          minHeight: isMobile ? undefined : "70vh",
          boxShadow: "0 40px 80px -30px rgba(0,0,0,0.6)",
        }}
        data-testid="ascent-panel"
      >
        {/* subtle inner glow at the top edge */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-24"
          style={{
            borderTopLeftRadius: 32,
            borderTopRightRadius: 32,
            background:
              "linear-gradient(180deg, rgba(200,154,78,0.14) 0%, rgba(200,154,78,0) 100%)",
          }}
        />

        {isMobile ? (
          <MobilePanel
            active={active}
            onSelect={commit}
            reduced={reduced}
          />
        ) : (
          <DesktopPanel
            active={active}
            camp={camp}
            progress={progress}
            onSelect={commit}
            onReplay={replay}
            reduced={reduced}
          />
        )}
      </motion.div>
    </div>
  );
};

/* ============================================================
 * Desktop panel — horizontal ridgeline on top, 2-col stage below
 * ============================================================ */
const DesktopPanel = ({ active, camp, progress, onSelect, onReplay, reduced }) => {
  return (
    <div className="relative flex h-full min-h-[70vh] flex-col p-8 md:p-12">
      {/* PANEL HEADER — meta */}
      <div className="flex items-center justify-between">
        <span
          className="label-caps text-[11px]"
          style={{ color: "rgba(174,196,199,0.7)" }}
        >
          The Ascent — your lease-up, live
        </span>
        <span
          className="label-caps text-[11px]"
          style={{ color: "rgba(174,196,199,0.7)" }}
        >
          Step {String(active + 1).padStart(2, "0")} of {CAMPS.length}
        </span>
      </div>

      {/* TRAIL — ridgeline SVG with camp nodes overlaid */}
      <div className="relative mt-6" style={{ height: 210 }}>
        <TrailSVG progress={progress} reduced={reduced} />
        <div className="absolute inset-0">
          {CAMPS.map((c, i) => {
            const reached = i <= active;
            const isCurrent = i === active;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => onSelect(i)}
                onMouseEnter={() => onSelect(i)}
                aria-label={`${c.label} · ${c.time}`}
                data-testid={`ascent-camp-${i}`}
                data-active={reached}
                className="group absolute flex flex-col items-center focus:outline-none"
                style={{
                  left: `${(c.x / 1200) * 100}%`,
                  top: `${(c.y / 210) * 100}%`,
                  transform: "translate(-50%,-50%)",
                }}
              >
                {isCurrent && (
                  <motion.span
                    aria-hidden
                    className="absolute rounded-full"
                    style={{ border: "1.5px solid rgba(227,178,104,0.7)" }}
                    initial={{ width: 20, height: 20, opacity: 0.8 }}
                    animate={
                      reduced
                        ? { width: 20, height: 20, opacity: 0 }
                        : { width: 56, height: 56, opacity: 0 }
                    }
                    transition={{
                      duration: 1.6,
                      repeat: reduced ? 0 : Infinity,
                      ease: "easeOut",
                    }}
                  />
                )}
                <motion.span
                  className="block rounded-full"
                  animate={{
                    scale: isCurrent ? 1.15 : reached ? 1 : 0.85,
                    backgroundColor: reached ? "#E3B268" : "rgba(20,35,27,0.85)",
                    borderColor: reached ? "#F8F5EF" : "rgba(174,196,199,0.35)",
                  }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  style={{
                    width: 12,
                    height: 12,
                    border: "1.5px solid",
                    boxShadow: reached
                      ? "0 0 12px rgba(227,178,104,0.7)"
                      : "none",
                  }}
                />
                <span
                  className="label-caps mt-3 whitespace-nowrap text-[10px]"
                  style={{
                    color: reached ? "#F8F5EF" : "rgba(237,233,224,0.45)",
                    transition: "color 0.35s",
                  }}
                >
                  {c.label} · {c.time}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* STAGE — two column: copy left, artifact right */}
      <div className="mt-4 grid flex-1 grid-cols-1 items-center gap-10 md:grid-cols-12 md:gap-8">
        <div className="md:col-span-7">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={camp.key}
              initial={
                reduced
                  ? { opacity: 0 }
                  : { opacity: 0, y: 24, filter: "blur(3px)" }
              }
              animate={
                reduced
                  ? { opacity: 1, transition: { duration: 0.2 } }
                  : {
                      opacity: 1,
                      y: 0,
                      filter: "blur(0px)",
                      transition: {
                        duration: 0.38,
                        ease: [0.22, 1, 0.36, 1],
                      },
                    }
              }
              exit={
                reduced
                  ? { opacity: 0, transition: { duration: 0.2 } }
                  : {
                      opacity: 0,
                      y: -20,
                      filter: "blur(3px)",
                      transition: { duration: 0.25 },
                    }
              }
              data-testid="ascent-stage-copy"
            >
              <p
                className="font-display"
                style={{
                  fontSize: "clamp(56px,7vw,96px)",
                  color: "#E3B268",
                  lineHeight: 0.95,
                  letterSpacing: "-0.02em",
                }}
                data-testid="ascent-day-big"
              >
                {camp.dayBig}
              </p>
              <h3
                className="font-display mt-4"
                style={{
                  fontSize: "clamp(26px,2.6vw,36px)",
                  color: "#F8F5EF",
                  lineHeight: 1.1,
                }}
                data-testid="ascent-camp-title"
              >
                {camp.title}
              </h3>
              <ul className="mt-8">
                {camp.lines.map((line, i) => (
                  <li
                    key={i}
                    className="border-t py-4 text-[15px] leading-[1.5]"
                    style={{
                      borderColor: "rgba(237,233,224,0.12)",
                      color: "rgba(237,233,224,0.82)",
                    }}
                  >
                    {line}
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="md:col-span-5">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={`chip-${camp.key}`}
              initial={
                reduced
                  ? { opacity: 0 }
                  : {
                      opacity: 0,
                      y: 24,
                      rotate: -2,
                      filter: "blur(3px)",
                    }
              }
              animate={
                reduced
                  ? { opacity: 1, transition: { duration: 0.2 } }
                  : {
                      opacity: 1,
                      y: 0,
                      rotate: -2,
                      filter: "blur(0px)",
                      transition: {
                        duration: 0.38,
                        ease: [0.22, 1, 0.36, 1],
                      },
                    }
              }
              exit={
                reduced
                  ? { opacity: 0, transition: { duration: 0.2 } }
                  : {
                      opacity: 0,
                      y: -20,
                      filter: "blur(3px)",
                      transition: { duration: 0.25 },
                    }
              }
              data-testid="ascent-stage-artifact"
            >
              <ArtifactChip campKey={camp.key} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* replay CTA — only at summit */}
      {active === CAMPS.length - 1 && (
        <motion.button
          type="button"
          onClick={onReplay}
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 8 }}
          animate={
            reduced
              ? { opacity: 1, transition: { duration: 0.2 } }
              : { opacity: 1, y: 0, transition: { duration: 0.4, delay: 0.3 } }
          }
          className="mt-6 inline-flex w-max items-center gap-2 self-start rounded-full px-4 py-2 text-[12px]"
          style={{
            border: "1px solid rgba(200,154,78,0.4)",
            color: "#E3B268",
            backgroundColor: "rgba(200,154,78,0.08)",
          }}
          data-testid="ascent-replay"
        >
          <RotateCcw size={13} strokeWidth={1.8} />
          <span className="label-caps">Replay the ascent</span>
        </motion.button>
      )}
    </div>
  );
};

/* ============================================================
 * Trail SVG — dashed background path + a solid larch overlay whose
 * pathLength animates to match the current progress fraction.
 * ============================================================ */
const TrailSVG = ({ progress, reduced }) => (
  <svg
    className="absolute inset-0 h-full w-full"
    viewBox="0 0 1200 210"
    preserveAspectRatio="none"
    fill="none"
    aria-hidden="true"
  >
    {/* not-yet-reached: dashed at 30% opacity */}
    <path
      d={PATH_D}
      stroke="rgba(174,196,199,0.3)"
      strokeWidth="1.5"
      strokeDasharray="4 6"
      vectorEffect="non-scaling-stroke"
    />
    {/* reached: solid larch line drawn to `progress` */}
    <motion.path
      d={PATH_D}
      stroke="#E3B268"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
      initial={false}
      animate={{ pathLength: progress }}
      transition={
        reduced
          ? { duration: 0 }
          : { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
      }
    />
    {/* glow under the reached line */}
    <motion.path
      d={PATH_D}
      stroke="#C89A4E"
      strokeWidth="8"
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
      style={{ filter: "blur(6px)", opacity: 0.45 }}
      initial={false}
      animate={{ pathLength: progress }}
      transition={
        reduced
          ? { duration: 0 }
          : { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
      }
    />
  </svg>
);

/* ============================================================
 * ArtifactChip — a paper card tuned to the active camp.
 * Each variant proves the promise the copy on the left just made.
 * ============================================================ */
const ArtifactChip = ({ campKey }) => {
  const CARD = {
    borderRadius: 18,
    backgroundColor: "#F8F5EF",
    padding: "22px 22px 20px",
    boxShadow: "0 30px 50px -20px rgba(0,0,0,0.55)",
  };
  switch (campKey) {
    case "basecamp":
      return (
        <article style={CARD} data-testid="artifact-basecamp">
          <header className="flex items-center justify-between">
            <span
              className="label-caps text-[10px]"
              style={{ color: "rgba(32,30,26,0.55)" }}
            >
              Management Agreement
            </span>
            <span
              className="label-caps text-[9px]"
              style={{ color: "rgba(32,30,26,0.4)" }}
            >
              Executed
            </span>
          </header>
          <div className="mt-5 flex items-center gap-4">
            <span
              className="grid h-10 w-10 place-items-center rounded-full"
              style={{ backgroundColor: "#E3B268" }}
            >
              <CheckCircle2 size={20} color="#14231B" strokeWidth={2.2} />
            </span>
            <div>
              <p
                className="font-display"
                style={{ fontSize: 24, color: "#201E1A", lineHeight: 1 }}
              >
                Signed.
              </p>
              <p
                className="mt-1 text-[13px]"
                style={{ color: "rgba(32,30,26,0.6)" }}
              >
                Photographer booked · Comps pulled · Intake sent
              </p>
            </div>
          </div>
          <div
            className="mt-5 flex items-center justify-between text-[10px]"
            style={{ color: "rgba(32,30,26,0.45)" }}
          >
            <span>Effective · Today, 09:14</span>
            <span
              className="italic"
              style={{ color: "#C89A4E", fontSize: 22, fontFamily: "'Cormorant Garamond', serif" }}
            >
              — the owner
            </span>
          </div>
        </article>
      );

    case "camp-1":
      return (
        <article style={CARD} data-testid="artifact-camp-1">
          <header className="flex items-center gap-2">
            <Camera size={14} color="#201E1A" />
            <span
              className="label-caps text-[10px]"
              style={{ color: "rgba(32,30,26,0.55)" }}
            >
              Shoot delivered
            </span>
          </header>
          <div className="mt-6 flex items-end gap-6">
            <div>
              <p
                className="font-display"
                style={{ fontSize: 56, color: "#201E1A", lineHeight: 0.9 }}
              >
                27
              </p>
              <p
                className="label-caps mt-1 text-[9px]"
                style={{ color: "rgba(32,30,26,0.55)" }}
              >
                Photos
              </p>
            </div>
            <div>
              <p
                className="font-display"
                style={{ fontSize: 56, color: "#E3B268", lineHeight: 0.9 }}
              >
                1
              </p>
              <p
                className="label-caps mt-1 text-[9px]"
                style={{ color: "rgba(32,30,26,0.55)" }}
              >
                Cinematic film
              </p>
            </div>
          </div>
          <div
            className="mt-5 rounded-lg px-3 py-2 text-[11px]"
            style={{
              backgroundColor: "rgba(200,154,78,0.14)",
              color: "#8A6220",
            }}
          >
            Delivered · Hour 47
          </div>
        </article>
      );

    case "camp-2": {
      const platforms = [
        "RentFaster",
        "Rentals.ca",
        "Zumper",
        "Kijiji",
        "Facebook",
        "Instagram",
      ];
      return (
        <article style={CARD} data-testid="artifact-camp-2">
          <header className="flex items-center justify-between">
            <span
              className="label-caps text-[10px]"
              style={{ color: "rgba(32,30,26,0.55)" }}
            >
              Live checklist
            </span>
            <span
              className="label-caps text-[9px]"
              style={{ color: "rgba(32,30,26,0.4)" }}
            >
              Day 4 · 14:32
            </span>
          </header>
          <ul className="mt-4 grid grid-cols-2 gap-y-2">
            {platforms.map((p) => (
              <li key={p} className="flex items-center gap-2 text-[12.5px]" style={{ color: "#201E1A" }}>
                <CheckCircle2 size={14} color="#C89A4E" strokeWidth={2.2} />
                {p}
              </li>
            ))}
          </ul>
          <p
            className="mt-4 text-[11px] italic"
            style={{ color: "rgba(32,30,26,0.55)" }}
          >
            Every listing human-verified before it goes live.
          </p>
        </article>
      );
    }

    case "camp-3":
      return (
        <article style={CARD} data-testid="artifact-camp-3">
          <header className="flex items-center justify-between">
            <span
              className="label-caps text-[10px]"
              style={{ color: "rgba(32,30,26,0.55)" }}
            >
              Monday Report · Week 1
            </span>
            <span
              className="label-caps text-[9px]"
              style={{ color: "rgba(32,30,26,0.4)" }}
            >
              10:00 am
            </span>
          </header>
          <div className="mt-5 grid grid-cols-3 gap-3">
            <Stat n="41" l="Inquiries" />
            <Stat n="6" l="Showings" />
            <Stat n="2" l="Applications" />
          </div>
          <div
            className="mt-5 border-t pt-3"
            style={{ borderColor: "rgba(32,30,26,0.12)" }}
          >
            <p
              className="label-caps text-[9px]"
              style={{ color: "#C89A4E" }}
            >
              Our read
            </p>
            <p
              className="mt-1 text-[13px] leading-[1.4]"
              style={{ color: "#201E1A" }}
            >
              Price is right, lease expected this week.
            </p>
          </div>
        </article>
      );

    case "camp-4":
      return (
        <article style={CARD} data-testid="artifact-camp-4">
          <header className="flex items-center gap-2">
            <FileSignature size={14} color="#201E1A" />
            <span
              className="label-caps text-[10px]"
              style={{ color: "rgba(32,30,26,0.55)" }}
            >
              Application · The Larchmont
            </span>
          </header>
          <div className="mt-5 flex items-center gap-4">
            <span
              className="grid h-10 w-10 place-items-center rounded-full"
              style={{ backgroundColor: "#24382E" }}
            >
              <FileCheck2 size={18} color="#E3B268" strokeWidth={2.2} />
            </span>
            <div>
              <p
                className="font-display"
                style={{ fontSize: 22, color: "#201E1A", lineHeight: 1 }}
              >
                Approved.
              </p>
              <p
                className="mt-1 text-[12.5px]"
                style={{ color: "rgba(32,30,26,0.6)" }}
              >
                Credit A · Income 3.4× · Two positive references
              </p>
            </div>
          </div>
          <div
            className="mt-5 rounded-lg px-3 py-2 text-[11px]"
            style={{
              backgroundColor: "rgba(36,56,46,0.06)",
              color: "#24382E",
            }}
          >
            One clear recommendation — awaiting owner sign-off
          </div>
        </article>
      );

    case "summit":
    default:
      return (
        <article style={CARD} data-testid="artifact-summit">
          <header className="flex items-center justify-between">
            <span
              className="label-caps text-[10px]"
              style={{ color: "rgba(32,30,26,0.55)" }}
            >
              Move-in · Complete
            </span>
            <span
              className="label-caps text-[9px]"
              style={{ color: "rgba(32,30,26,0.4)" }}
            >
              10:12 am
            </span>
          </header>
          <div className="mt-5 flex items-center gap-4">
            <span
              className="grid h-12 w-12 place-items-center rounded-full"
              style={{
                backgroundColor: "#E3B268",
                boxShadow: "0 8px 22px -8px rgba(200,154,78,0.7)",
              }}
            >
              <KeyRound size={22} color="#14231B" strokeWidth={2.2} />
            </span>
            <div>
              <p
                className="font-display"
                style={{ fontSize: 24, color: "#201E1A", lineHeight: 1 }}
              >
                Keys handed over.
              </p>
              <p
                className="mt-1 text-[12.5px]"
                style={{ color: "rgba(32,30,26,0.6)" }}
              >
                Deposit secured · First month cleared
              </p>
            </div>
          </div>
          <p
            className="mt-5 border-t pt-3 text-[12.5px] italic"
            style={{
              borderColor: "rgba(32,30,26,0.12)",
              color: "rgba(32,30,26,0.7)",
            }}
          >
            The climb becomes cash flow.
          </p>
        </article>
      );
  }
};

const Stat = ({ n, l }) => (
  <div>
    <p
      className="font-display"
      style={{ fontSize: 26, color: "#24382E", lineHeight: 1 }}
    >
      {n}
    </p>
    <p
      className="label-caps mt-1 text-[9px]"
      style={{ color: "rgba(32,30,26,0.55)" }}
    >
      {l}
    </p>
  </div>
);

/* ============================================================
 * MobilePanel — vertical trail line on the left, stacked content
 * on the right, tap or swipe to change active camp.
 * ============================================================ */
const MobilePanel = ({ active, onSelect, reduced }) => {
  const camp = CAMPS[active];
  return (
    <div className="relative p-6 pb-10">
      <div className="flex items-center justify-between">
        <span
          className="label-caps text-[10px]"
          style={{ color: "rgba(174,196,199,0.7)" }}
        >
          The Ascent
        </span>
        <span
          className="label-caps text-[10px]"
          style={{ color: "rgba(174,196,199,0.7)" }}
        >
          Step {String(active + 1).padStart(2, "0")}/{CAMPS.length}
        </span>
      </div>

      <div className="mt-6 flex gap-5">
        {/* Vertical trail with 6 nodes */}
        <div className="relative flex flex-col items-center" style={{ width: 18 }}>
          <span
            aria-hidden
            className="absolute inset-y-2 left-1/2 -translate-x-1/2"
            style={{
              width: 1,
              borderLeft: "1px dashed rgba(174,196,199,0.3)",
            }}
          />
          <motion.span
            aria-hidden
            className="absolute left-1/2 top-2 -translate-x-1/2"
            style={{
              width: 2,
              backgroundColor: "#E3B268",
              boxShadow: "0 0 6px rgba(227,178,104,0.7)",
            }}
            initial={false}
            animate={{
              height: `${(active / (CAMPS.length - 1)) * 100}%`,
            }}
            transition={
              reduced ? { duration: 0 } : { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
            }
          />
          {CAMPS.map((c, i) => {
            const reached = i <= active;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => onSelect(i)}
                aria-label={c.label}
                data-testid={`ascent-camp-${i}`}
                className="relative z-10 flex-1 focus:outline-none"
                style={{ padding: "4px 0" }}
              >
                <motion.span
                  className="block rounded-full"
                  animate={{
                    scale: i === active ? 1.15 : reached ? 1 : 0.85,
                    backgroundColor: reached ? "#E3B268" : "rgba(20,35,27,0.85)",
                  }}
                  transition={{ duration: 0.3 }}
                  style={{
                    width: 10,
                    height: 10,
                    border: "1.5px solid",
                    borderColor: reached ? "#F8F5EF" : "rgba(174,196,199,0.35)",
                  }}
                />
              </button>
            );
          })}
        </div>

        {/* Stacked content */}
        <div className="flex-1 min-w-0">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={camp.key}
              initial={
                reduced
                  ? { opacity: 0 }
                  : { opacity: 0, y: 20, filter: "blur(3px)" }
              }
              animate={
                reduced
                  ? { opacity: 1, transition: { duration: 0.2 } }
                  : {
                      opacity: 1,
                      y: 0,
                      filter: "blur(0px)",
                      transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] },
                    }
              }
              exit={
                reduced
                  ? { opacity: 0, transition: { duration: 0.2 } }
                  : {
                      opacity: 0,
                      y: -20,
                      filter: "blur(3px)",
                      transition: { duration: 0.25 },
                    }
              }
            >
              <p
                className="label-caps text-[10px]"
                style={{ color: "#C89A4E" }}
              >
                {camp.label} · {camp.time}
              </p>
              <p
                className="font-display mt-2"
                style={{
                  fontSize: 44,
                  color: "#E3B268",
                  lineHeight: 0.95,
                }}
              >
                {camp.dayBig}
              </p>
              <h3
                className="font-display mt-3"
                style={{ fontSize: 22, color: "#F8F5EF", lineHeight: 1.1 }}
              >
                {camp.title}
              </h3>
              <ul className="mt-5">
                {camp.lines.map((line, i) => (
                  <li
                    key={i}
                    className="border-t py-3 text-[14px] leading-[1.5]"
                    style={{
                      borderColor: "rgba(237,233,224,0.12)",
                      color: "rgba(237,233,224,0.82)",
                    }}
                  >
                    {line}
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <ArtifactChip campKey={camp.key} />
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <p
        className="label-caps mt-6 text-center text-[9px]"
        style={{ color: "rgba(174,196,199,0.5)" }}
      >
        Tap or swipe to advance
      </p>
    </div>
  );
};

export const Ascent = () => {
  return (
    <section
      className="grain relative"
      style={{ backgroundColor: "#14231B" }}
      data-nav-theme="dark"
      data-testid="home-ascent"
    >
      <div className="relative z-10">
        <Intro />
        <ExpeditionPanel />
        <Afterword />
      </div>
    </section>
  );
};

export default Ascent;
